import { Field, Limit } from './use-input-field-rules';
import {
  compareMeasure,
  debugInfo,
  InputFieldType,
  isMeasureWithValue,
  measureEquals,
  minMeasure,
  negateMeasure,
  Nullable,
  NullableMeasure,
  sumMeasures,
} from '@e2m-customer-portal/shared';
import { ForecastPageFieldLimit, ForecastPageLimitsResponse, LimitSpecification } from '../../generated/graphql';

interface FCRRule {
  desc: string;
  limit: () => NullableMeasure | undefined;
  limitSpecification: LimitSpecification;
  flag?: 'min' | 'max';
}

const ZERO_MEASURE = { value: 0, unit: 'kW' } as const;

const COLOR_BLUE = 'color: blue' as const;
const COLOR_GREEN = 'color: green' as const;
const PRODUCTION = 'Production: ' as const;

function logFcr(
  ruleId: string,
  firstTitle: string,
  firstValue: NullableMeasure,
  secondTitle: string,
  secondValue: NullableMeasure,
  result: NullableMeasure,
): void {
  debugInfo(
    '%c%s%c%s%s%s%s%c%s%s',
    'font-weight: bold',
    `➤ ${ruleId} considered:\n`,
    COLOR_GREEN,
    `${firstTitle}: `,
    `${firstValue.value} ${firstValue.unit}\n`,
    `${secondTitle}: `,
    `${secondValue.value} ${secondValue.unit}\n`,
    COLOR_BLUE,
    'limit: ',
    `${result.value} ${result.unit}`,
  );
}

function getFcrErrors(
  fcrLimits: ForecastPageFieldLimit | undefined,
  prodField: Field | undefined,
  installedPower: NullableMeasure,
  minimum: NullableMeasure,
  maximum: NullableMeasure,
): FCRRule[] {
  return [
    {
      desc: 'FCR higher than smallest band (FCR_01)',
      limit: () => {
        if (fcrLimits) {
          logFcr(
            'FCR_01',
            'Negative band: ',
            fcrLimits.negativeBand as NullableMeasure,
            'Positive band: ',
            fcrLimits.positiveBand as NullableMeasure,
            minMeasure(fcrLimits.positiveBand, fcrLimits.negativeBand),
          );

          return minMeasure(fcrLimits.positiveBand, fcrLimits.negativeBand);
        }
      },
      limitSpecification: LimitSpecification.FCR,
      flag: 'max',
    },
    {
      desc: 'FCR higher than contractual limit minus production (FCR_03)',
      limit: () => {
        if (
          fcrLimits &&
          prodField &&
          isMeasureWithValue(prodField?.value) &&
          fcrLimits.upperLimit.limitSpecification === LimitSpecification.CONTRACTUAL
        ) {
          const limit = sumMeasures(fcrLimits.upperLimit.limit, negateMeasure(prodField.value));
          logFcr(
            'FCR_03',
            'Contractual upper limit: ',
            fcrLimits.upperLimit.limit,
            PRODUCTION,
            prodField.value,
            isMeasureWithValue(limit) && limit.value > 0 ? limit : ZERO_MEASURE,
          );

          return isMeasureWithValue(limit) && limit.value > 0 ? limit : ZERO_MEASURE;
        }
      },
      limitSpecification: LimitSpecification.CONTRACTUAL,
      flag: 'max',
    },
    {
      desc: 'FCR higher than technical limit minus production (FCR_04)',
      limit: () => {
        if (
          fcrLimits &&
          prodField &&
          isMeasureWithValue(prodField?.value) &&
          fcrLimits.upperLimit.limitSpecification === LimitSpecification.TECHNICAL
        ) {
          const limit = sumMeasures(fcrLimits.upperLimit.limit, negateMeasure(prodField.value));
          logFcr(
            'FCR_04',
            'Technical upper limit: ',
            fcrLimits.upperLimit.limit,
            PRODUCTION,
            prodField.value,
            isMeasureWithValue(limit) && limit.value > 0 ? limit : ZERO_MEASURE,
          );

          return isMeasureWithValue(limit) && limit.value > 0 ? limit : ZERO_MEASURE;
        }
      },
      limitSpecification: LimitSpecification.TECHNICAL,
      flag: 'max',
    },
    {
      desc: 'FCR higher than template limit minus production (FCR_10)',
      limit: () => {
        if (
          fcrLimits &&
          prodField &&
          isMeasureWithValue(prodField?.value) &&
          fcrLimits.upperLimit.limitSpecification === LimitSpecification.TEMPLATE
        ) {
          const limit = sumMeasures(maximum, negateMeasure(prodField.value));
          logFcr(
            'FCR_10',
            'Maximum: ',
            maximum,
            PRODUCTION,
            prodField.value,
            isMeasureWithValue(limit) && limit.value > 0 ? limit : ZERO_MEASURE,
          );

          return isMeasureWithValue(limit) && limit.value > 0 ? limit : ZERO_MEASURE;
        }
      },
      limitSpecification: LimitSpecification.TEMPLATE,
      flag: 'max',
    },
    {
      desc: 'FCR higher than production minus contractual limit (FCR_05)',
      limit: () => {
        if (
          fcrLimits &&
          prodField &&
          isMeasureWithValue(prodField?.value) &&
          fcrLimits.lowerLimit.limitSpecification === LimitSpecification.CONTRACTUAL
        ) {
          const limit = sumMeasures(prodField.value, negateMeasure(fcrLimits.lowerLimit.limit));
          logFcr(
            'FCR_05',
            PRODUCTION,
            prodField.value,
            'Contractual lower limit: ',
            fcrLimits.lowerLimit.limit,
            limit.value > 0 ? limit : ZERO_MEASURE,
          );

          return limit.value > 0 ? limit : ZERO_MEASURE;
        }
        return installedPower;
      },
      limitSpecification: LimitSpecification.CONTRACTUAL,
      flag: 'min',
    },
    {
      desc: 'FCR higher than production minus technical limit (FCR_06)',
      limit: () => {
        if (
          fcrLimits &&
          prodField &&
          isMeasureWithValue(prodField.value) &&
          fcrLimits.lowerLimit.limitSpecification === LimitSpecification.TECHNICAL
        ) {
          const limit = sumMeasures(prodField.value, negateMeasure(fcrLimits.lowerLimit.limit));
          logFcr(
            'FCR_06',
            PRODUCTION,
            prodField.value,
            'Technical lower limit: ',
            fcrLimits.lowerLimit.limit,
            limit.value > 0 ? limit : ZERO_MEASURE,
          );

          return limit.value > 0 ? limit : ZERO_MEASURE;
        }
        return installedPower;
      },
      limitSpecification: LimitSpecification.TECHNICAL,
      flag: 'min',
    },
    {
      desc: 'FCR higher than production minus template limit (FCR_11)',
      limit: () => {
        if (
          fcrLimits &&
          prodField &&
          isMeasureWithValue(minimum) &&
          isMeasureWithValue(prodField.value) &&
          fcrLimits.lowerLimit.limitSpecification === LimitSpecification.TEMPLATE
        ) {
          const limit = sumMeasures(prodField.value, negateMeasure(minimum));
          logFcr('FCR_11', PRODUCTION, prodField.value, 'Minimum: ', minimum, limit.value > 0 ? limit : ZERO_MEASURE);

          return limit.value > 0 ? limit : ZERO_MEASURE;
        }
        return installedPower;
      },
      limitSpecification: LimitSpecification.TEMPLATE,
      flag: 'min',
    },
  ];
}

export function handleFcrErrors(
  fields: undefined | readonly Field[],
  limits: ForecastPageLimitsResponse,
  minimum: NullableMeasure,
  maximum: NullableMeasure,
): Limit | null {
  const prodField = fields?.find((f) => f.type === InputFieldType.PRODUCTION);

  if (
    isMeasureWithValue(prodField, 'value') &&
    (measureEquals(ZERO_MEASURE, prodField.value) || measureEquals(limits.installedPower, prodField.value))
  ) {
    // Production is at minimum or maximum, FCR has to be zero. No validation needed,
    // because the field is disabled.
    return { limit: ZERO_MEASURE, limitSpecification: LimitSpecification.FCR };
  }

  const fcrLimits = limits.fields.find((f) => f.type === InputFieldType.FCR);
  let fcrLimitsCalculated: ForecastPageFieldLimit | undefined;
  if (
    (fcrLimits?.positiveBand?.value === null && fcrLimits?.negativeBand?.value === null) ||
    (fcrLimits?.positiveBand?.value && fcrLimits?.positiveBand.value >= fcrLimits?.upperLimit.limit.value)
  ) {
    const fcrBand = Math.abs((fcrLimits?.upperLimit.limit.value - fcrLimits?.lowerLimit.limit.value) / 2);
    // tha calculated frc needs to be positive and need to be rounded to the next 1000,
    // otherwise we will get a float value after the conversion in kW, which is not allowed.
    const calculatedFcrValue = Math.round(fcrBand / 1000) * 1000;
    fcrLimitsCalculated = {
      ...fcrLimits,
      negativeBand: {
        __typename: 'NullableMeasure',
        value: calculatedFcrValue,
        unit: fcrLimits.negativeBand?.unit ?? 'W',
      },
      positiveBand: { __typename: 'NullableMeasure', value: calculatedFcrValue, unit: fcrLimits.positiveBand.unit },
    };
  } else {
    fcrLimitsCalculated = fcrLimits;
  }
  const rules = getFcrErrors(fcrLimitsCalculated, prodField, limits.installedPower, minimum, maximum);

  const [maxRule, maxLimit] = rules
    .map((rule) => [rule, rule.limit()] as const)
    .filter((t): t is [FCRRule, NullableMeasure] => t[1] != null)
    .reduce((p, c) => (compareMeasure(p[1], c[1]) > 0 ? c : p));

  debugInfo(
    '%c%s%c%s%s%c%s%s%s%s%c%s%s%c%s%s%s%s',
    'font-weight: bold',
    '✅ Returned FCR limits: \n\n',
    COLOR_GREEN,
    'maxLimit: ',
    maxRule.desc,
    COLOR_BLUE,
    '\nlimit: ',
    isMeasureWithValue(maxLimit) ? `${maxLimit.value} ${maxLimit.unit}` : `${ZERO_MEASURE.value} ${ZERO_MEASURE.unit}`,
    '\nlimitSpecification: ',
    maxRule.limitSpecification,
  );

  return {
    limit: isMeasureWithValue(maxLimit) ? maxLimit : ZERO_MEASURE,
    limitSpecification: maxRule.limitSpecification,
    flag: maxRule.flag,
  };
}

export function getLowerLimit(
  type: InputFieldType,
  limits: ForecastPageLimitsResponse,
  templateMinimum: NullableMeasure,
): Limit | null {
  if (type === InputFieldType.FCR) {
    return null;
  }

  let minLimit: Nullable<Limit> = limits.fields.find((f) => f.type === type)?.lowerLimit;

  if (
    type !== InputFieldType.PRODUCTION &&
    ((minLimit?.limitSpecification === LimitSpecification.TEMPLATE &&
      compareMeasure(minLimit.limit, templateMinimum) > 0) ||
      (minLimit && compareMeasure(minLimit.limit, templateMinimum) < 0))
  ) {
    minLimit = {
      limit: isMeasureWithValue(templateMinimum) ? templateMinimum : minLimit.limit,
      limitSpecification: LimitSpecification.TEMPLATE,
    };
  }

  return minLimit ?? null;
}

export function getUpperLimit(
  type: InputFieldType,
  fields: undefined | readonly Field[],
  limits: ForecastPageLimitsResponse,
  templateMinimum: NullableMeasure,
  templateMaximum: NullableMeasure,
): Limit | null {
  if (type === InputFieldType.FCR) {
    return handleFcrErrors(fields, limits, templateMinimum, templateMaximum);
  }

  let maxLimit: Nullable<Limit> = limits.fields.find((f) => f.type === type)?.upperLimit;

  const pairs = {
    [InputFieldType.AFRR_LOWER_LIMIT]: InputFieldType.AFRR_UPPER_LIMIT,
    [InputFieldType.MFRR_LOWER_LIMIT]: InputFieldType.MFRR_UPPER_LIMIT,
    [InputFieldType.FLEX_PLUS_LOWER_LIMIT]: InputFieldType.FLEX_PLUS_UPPER_LIMIT,
  };

  const pairedField = fields?.find((f) => f.type === pairs[type as keyof typeof pairs]);
  if (
    pairedField != null &&
    isMeasureWithValue(pairedField.value) &&
    (maxLimit == null || compareMeasure(maxLimit.limit, pairedField.value) > 0)
  ) {
    maxLimit = {
      limit: pairedField.value,
      limitSpecification: LimitSpecification.PAIRED_MAXIMUM,
    };
  }

  if (
    type !== InputFieldType.PRODUCTION &&
    ((maxLimit?.limitSpecification === LimitSpecification.TEMPLATE &&
      compareMeasure(maxLimit.limit, templateMaximum) < 0) ||
      (maxLimit && compareMeasure(maxLimit.limit, templateMaximum) > 0))
  ) {
    maxLimit = {
      limit: isMeasureWithValue(templateMaximum) ? templateMaximum : maxLimit.limit,
      limitSpecification: LimitSpecification.TEMPLATE,
    };
  }

  return maxLimit ?? null;
}
